import type { RedoCart } from "@redotech/merchant-sdk/rpc/schema/redo-supplies/redo-cart";
import { useRequiredContext } from "@redotech/react-util/context";
import type { LocationId } from "@redotech/redo-inventory-id";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type { ProductFamilyId, ProductId } from "@redotech/redo-product-id";
import {
  isFreeLine,
  qualifyingCartValue,
} from "@redotech/redo-supplies-common/cart-value";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { useActiveSuppliesPromotion } from "./use-active-supplies-promotion";

export interface RedoSuppliesCartContextValue {
  cart: RedoCart | null;
  isLoading: boolean;
  isCartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  isFreePickerOpen: boolean;
  setFreePickerOpen: (open: boolean) => void;
  refreshCart: () => void;
  /** Resolves with whether this add pushed the paid cart past the promo threshold. */
  upsertItem: (
    productId: ProductId,
    productFamilyId: ProductFamilyId,
    quantityDelta: number,
    productInfo: {
      title: string;
      price: number;
      currency: string;
      imageUrl: string | null;
      options: { name: string; value: string }[];
      attributes?: { key: string; value: string }[];
    },
  ) => Promise<{ crossedThreshold: boolean }>;
  claimFreeItem: (
    productId: ProductId,
    productFamilyId: ProductFamilyId,
    productInfo: {
      title: string;
      price: number;
      currency: string;
      imageUrl: string | null;
      options: { name: string; value: string }[];
    },
  ) => Promise<void>;
  /** Attaches a reorder config to the already-claimed free line (no paid line). */
  attachReorderToFreeItem: (
    productId: ProductId,
    reorderAttributes: { key: string; value: string }[],
  ) => Promise<void>;
  setDestinationLocation: (locationId: LocationId | null) => Promise<void>;
}

const RedoSuppliesCartContext =
  createContext<RedoSuppliesCartContextValue | null>(null);

const CART_QUERY_KEY = ["redo-supplies-cart"];

export function useRedoSuppliesCart(): RedoSuppliesCartContextValue {
  const ctx = useContext(RedoSuppliesCartContext);
  if (!ctx) {
    throw new Error(
      "useRedoSuppliesCart must be used within a RedoSuppliesCartProvider",
    );
  }
  return ctx;
}

export const RedoSuppliesCartProvider = memo(function RedoSuppliesCartProvider({
  children,
}: {
  children: ReactNode;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const queryClient = useQueryClient();
  const [isCartOpen, setCartOpen] = useState(false);
  const [isFreePickerOpen, setFreePickerOpen] = useState(false);
  const promotion = useActiveSuppliesPromotion();

  const { data, isLoading } = useQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: () => rpcClient.getRedoCart({}),
  });

  const refreshCart = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
  }, [queryClient]);

  const upsertItem = useCallback(
    async (
      productId: ProductId,
      productFamilyId: ProductFamilyId,
      quantityDelta: number,
      productInfo: {
        title: string;
        price: number;
        currency: string;
        imageUrl: string | null;
        options: { name: string; value: string }[];
        attributes?: { key: string; value: string }[];
      },
    ) => {
      const beforeValue = data?.cart ? qualifyingCartValue(data.cart) : 0;
      const result = await rpcClient.upsertRedoCartItems({
        productId,
        productFamilyId,
        quantityDelta,
        ...productInfo,
      });
      // Set data for instant UI update, then invalidate to converge on
      // true server state in case concurrent requests resolve out of order.
      queryClient.setQueryData(CART_QUERY_KEY, result);
      void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });

      const afterItems = result.cart?.items ?? [];
      const afterValue = result.cart ? qualifyingCartValue(result.cart) : 0;
      const crossedThreshold =
        promotion != null &&
        beforeValue < promotion.thresholdAmount &&
        afterValue >= promotion.thresholdAmount &&
        !afterItems.some(isFreeLine);
      return { crossedThreshold };
    },
    [rpcClient, queryClient, data?.cart, promotion],
  );

  const claimFreeItem = useCallback(
    async (
      productId: ProductId,
      productFamilyId: ProductFamilyId,
      productInfo: {
        title: string;
        price: number;
        currency: string;
        imageUrl: string | null;
        options: { name: string; value: string }[];
      },
    ) => {
      const result = await rpcClient.upsertRedoCartItems({
        productId,
        productFamilyId,
        quantityDelta: 1,
        ...productInfo,
        claimFreeItem: true,
      });
      queryClient.setQueryData(CART_QUERY_KEY, result);
      void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
    },
    [rpcClient, queryClient],
  );

  const attachReorderToFreeItem = useCallback(
    async (
      productId: ProductId,
      reorderAttributes: { key: string; value: string }[],
    ) => {
      const result = await rpcClient.attachSuppliesReorderToFreeItem({
        productId,
        reorderAttributes,
      });
      queryClient.setQueryData(CART_QUERY_KEY, result);
      void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
    },
    [rpcClient, queryClient],
  );

  const setDestinationLocation = useCallback(
    async (locationId: LocationId | null) => {
      const result = await rpcClient.setCartDestinationLocation({ locationId });
      queryClient.setQueryData(CART_QUERY_KEY, result);
      void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
    },
    [rpcClient, queryClient],
  );

  const value: RedoSuppliesCartContextValue = {
    cart: data?.cart ?? null,
    isLoading,
    isCartOpen,
    setCartOpen,
    isFreePickerOpen,
    setFreePickerOpen,
    refreshCart,
    upsertItem,
    claimFreeItem,
    attachReorderToFreeItem,
    setDestinationLocation,
  };

  return (
    <RedoSuppliesCartContext.Provider value={value}>
      {children}
    </RedoSuppliesCartContext.Provider>
  );
});
